/**
 * Locatiefoto's in de callsheet + de Word-export (Joris, okt 2026).
 *
 * - Per locatie en per planningsregel foto's: slepen of kiezen, meerdere tegelijk, max 10 MB,
 *   jpg/png/heic/webp. De server controleert alles opnieuw (type uit de bytes, HEIC → JPG).
 * - Opslag privé (blob `callsheet-fotos`); de lijst komt met SAS-links van een uur. Verloopt een
 *   link (pagina lang open), dan haalt een kapotte afbeelding de lijst één keer opnieuw op.
 * - Vangnetten: bijschrift en volgorde → ongedaan maken (Ctrl+Z); verwijderen → bevestigen én
 *   ongedaan maken (de server verwijdert zacht).
 *
 * Bewust één bestand, gelijk op develop en main: de CallSheetManager verschilt per branch,
 * deze bouwstenen niet.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Box, Button, CircularProgress, Dialog, DialogContent, IconButton, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  AddAPhotoOutlined as AddPhotoIcon,
  ChevronLeft as VorigeIcon,
  ChevronRight as VolgendeIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  Description as WordIcon,
} from '@mui/icons-material';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { bevestig } from '../../utils/bevestig';
import { biedOngedaanAan } from '../../utils/ongedaan';
import { leesJson, meldFout } from '../../utils/apiFout';

export const MAX_FOTO_BYTES = 10 * 1024 * 1024;
const TOEGESTAAN = ['jpg', 'jpeg', 'png', 'heic', 'heif', 'webp'];
export const FOTO_ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif';
const GELIJKTIJDIG = 3;
const GRIJS = '#64748b';
const RAND = '#e2e8f0';

/** "locatie:<id>" / "planning:<id>" — dezelfde sleutel als de server gebruikt. */
export const doelSleutel = (doel) => (doel?.locatieId ? `locatie:${doel.locatieId}` : `planning:${doel?.planningId}`);
const sleutelVanFoto = (f) => (f.locatieId ? `locatie:${f.locatieId}` : `planning:${f.planningId}`);

/** Voorcontrole in de browser; de server is leidend. → { goed: File[], fout: [{naam, reden}] } */
export function controleerBestanden(lijst) {
  const goed = [];
  const fout = [];
  for (const f of Array.from(lijst || [])) {
    const ext = String(f.name || '').split('.').pop().toLowerCase();
    const typeOk = TOEGESTAAN.includes(ext) || /^image\/(jpeg|png|webp|heic|heif)$/.test(f.type || '');
    if (!typeOk) fout.push({ naam: f.name, reden: 'alleen jpg, png, heic of webp' });
    else if (f.size > MAX_FOTO_BYTES) fout.push({ naam: f.name, reden: 'groter dan 10 MB' });
    else goed.push(f);
  }
  return { goed, fout };
}

/** Bestandsnaam uit Content-Disposition (filename* wint, RFC 5987). */
export function naamUitHeader(kop) {
  const s = String(kop || '');
  const utf8 = s.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (utf8) { try { return decodeURIComponent(utf8[1].trim()); } catch { /* val terug */ } }
  const gewoon = s.match(/filename\s*=\s*"([^"]+)"/i) || s.match(/filename\s*=\s*([^;]+)/i);
  return gewoon ? gewoon[1].trim() : '';
}

/** Zelfde naam als de PDF ("Callsheet - <Titel> - <YYYY-MM-DD>"), maar .docx. */
export function wordBestandsnaam(fields = {}) {
  const schoon = String(fields.Titel || 'Callsheet').replace(/[\\/:*?"<>|\r\n]+/g, ' ').replace(/\s+/g, ' ').trim();
  const titel = Array.from(schoon).slice(0, 80).join('').trim() || 'Naamloos';
  const datumRaw = fields['Datum (handmatig)'] || fields.Datum;
  const datum = datumRaw ? String(datumRaw).slice(0, 10) : '';
  return `Callsheet - ${titel}${datum ? ` - ${datum}` : ''}.docx`;
}

/** Alle foto's van één callsheet + de acties erop. */
export function useCallsheetFotos(callsheetId) {
  const [fotos, setFotos] = useState([]);
  const [bezig, setBezig] = useState({});          // doelSleutel → "2/5"
  const basis = `${BACKEND_URL}/Api/callsheets/${encodeURIComponent(callsheetId || '')}/fotos`;
  const laatsteHerlaad = useRef(0);

  const laad = useCallback(async () => {
    if (!callsheetId) return;
    try {
      const d = await leesJson(await authFetch(basis), "Foto's laden");
      setFotos(Array.isArray(d?.fotos) ? d.fotos : []);
    } catch (e) { console.warn('[callsheet-fotos] laden:', e.message); }
  }, [basis, callsheetId]);

  useEffect(() => { laad(); }, [laad]);
  // SAS-links leven een uur; een lang openstaande pagina ververst ze ruim daarvoor.
  useEffect(() => {
    const t = setInterval(laad, 45 * 60 * 1000);
    return () => clearInterval(t);
  }, [laad]);

  /** Een afbeelding laadt niet (verlopen link): hooguit eens per minuut opnieuw ophalen. */
  const linkVerlopen = useCallback(() => {
    if (Date.now() - laatsteHerlaad.current < 60000) return;
    laatsteHerlaad.current = Date.now();
    laad();
  }, [laad]);

  const perDoel = useMemo(() => {
    const m = new Map();
    for (const f of fotos) {
      const k = sleutelVanFoto(f);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(f);
    }
    for (const lijst of m.values()) lijst.sort((a, b) => (a.volgorde - b.volgorde) || (a.id - b.id));
    return m;
  }, [fotos]);

  const voor = useCallback((doel) => perDoel.get(doelSleutel(doel)) || [], [perDoel]);

  const upload = useCallback(async (doel, bestanden) => {
    const { goed, fout } = controleerBestanden(bestanden);
    const sleutel = doelSleutel(doel);
    const geweigerd = [...fout];
    const teller = { klaar: 0 };
    const nieuw = [];
    if (goed.length) setBezig((b) => ({ ...b, [sleutel]: `0/${goed.length}` }));
    const wachtrij = [...goed];
    const werker = async () => {
      while (wachtrij.length) {
        const bestand = wachtrij.shift();
        const fd = new FormData();
        fd.append('files', bestand, bestand.name);
        if (doel.locatieId) fd.append('locatieId', doel.locatieId);
        if (doel.planningId) fd.append('planningId', doel.planningId);
        try {
          const d = await leesJson(await authFetch(basis, { method: 'POST', body: fd }), 'Foto uploaden');
          nieuw.push(...(d?.fotos || []));
          geweigerd.push(...(d?.geweigerd || []));
        } catch (e) {
          geweigerd.push({ naam: bestand.name, reden: e.message.replace(/^Foto uploaden mislukt\s*/, '') || 'mislukt' });
        }
        teller.klaar += 1;
        const stand = `${teller.klaar}/${goed.length}`;
        setBezig((b) => ({ ...b, [sleutel]: stand }));
      }
    };
    await Promise.all(Array.from({ length: Math.min(GELIJKTIJDIG, goed.length) }, werker));
    setBezig((b) => { const n = { ...b }; delete n[sleutel]; return n; });
    if (nieuw.length) {
      setFotos((oud) => [...oud, ...nieuw]);
      toast.success(nieuw.length === 1 ? 'Foto toegevoegd' : `${nieuw.length} foto's toegevoegd`);
    }
    if (geweigerd.length) {
      toast.error(`Niet toegevoegd:\n${geweigerd.map((g) => `• ${g.naam}: ${g.reden}`).join('\n')}`,
        { duration: 9000, style: { whiteSpace: 'pre-line' } });
    }
    return nieuw;
  }, [basis]);

  const zetBijschrift = useCallback(async (foto, tekst, { metOngedaan = true } = {}) => {
    const vorige = foto.bijschrift || '';
    if ((tekst || '').trim() === vorige) return;
    const patch = async (waarde) => {
      const d = await leesJson(await authFetch(`${basis}/${foto.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ bijschrift: waarde }),
      }), 'Bijschrift opslaan');
      setFotos((oud) => oud.map((f) => (f.id === foto.id ? { ...f, bijschrift: d.bijschrift } : f)));
    };
    try {
      await patch(tekst);
      if (metOngedaan) biedOngedaanAan('Bijschrift aangepast', () => patch(vorige), { eigenKnop: true });
    } catch (e) { meldFout(e, 'Bijschrift opslaan'); }
  }, [basis]);

  const herorden = useCallback(async (doel, ids) => {
    const vorige = (perDoel.get(doelSleutel(doel)) || []).map((f) => f.id);
    if (vorige.join(',') === ids.join(',')) return;
    const zet = async (volgorde) => {
      setFotos((oud) => oud.map((f) => (volgorde.includes(f.id) ? { ...f, volgorde: volgorde.indexOf(f.id) } : f)));
      await leesJson(await authFetch(`${basis}/volgorde`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids: volgorde }),
      }), 'Volgorde opslaan');
    };
    try {
      await zet(ids);
      biedOngedaanAan('Volgorde aangepast', () => zet(vorige), { eigenKnop: true });
    } catch (e) { meldFout(e, 'Volgorde opslaan'); laad(); }
  }, [basis, perDoel, laad]);

  const verwijder = useCallback(async (foto) => {
    const ok = await bevestig({
      titel: 'Foto verwijderen?',
      tekst: 'De foto verdwijnt van de callsheet, uit de crew-app, de PDF en de Word-export. Je kunt het daarna nog ongedaan maken.',
      knop: 'Verwijderen', gevaarlijk: true,
    });
    if (!ok) return false;
    try {
      await leesJson(await authFetch(`${basis}/${foto.id}`, { method: 'DELETE' }), 'Foto verwijderen');
      setFotos((oud) => oud.filter((f) => f.id !== foto.id));
      biedOngedaanAan('Foto verwijderd', async () => {
        const terug = await leesJson(await authFetch(`${basis}/${foto.id}/herstel`, { method: 'POST' }), 'Foto terugzetten');
        setFotos((oud) => [...oud.filter((f) => f.id !== terug.id), terug]);
      }, { eigenKnop: true });
      return true;
    } catch (e) { meldFout(e, 'Foto verwijderen'); return false; }
  }, [basis]);

  return { fotos, voor, bezig, laad, upload, zetBijschrift, herorden, verwijder, linkVerlopen };
}

/** Grote weergave met bijschrift, vorige/volgende en verwijderen. */
function FotoLightbox({ fotos, index, onSluit, onIndex, api, bewerkbaar }) {
  const foto = fotos[index];
  const [tekst, setTekst] = useState(foto?.bijschrift || '');
  useEffect(() => { setTekst(foto?.bijschrift || ''); }, [foto?.id, foto?.bijschrift]);
  useEffect(() => {
    if (!foto) return undefined;
    const toets = (e) => {
      const tag = (document.activeElement?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      if (e.key === 'ArrowLeft' && index > 0) onIndex(index - 1);
      if (e.key === 'ArrowRight' && index < fotos.length - 1) onIndex(index + 1);
    };
    window.addEventListener('keydown', toets);
    return () => window.removeEventListener('keydown', toets);
  }, [foto, index, fotos.length, onIndex]);
  if (!foto) return null;
  const opslaan = () => api.zetBijschrift(foto, tekst);
  return (
    <Dialog open onClose={() => { opslaan(); onSluit(); }} maxWidth="md" fullWidth>
      <DialogContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" sx={{ color: GRIJS }}>{index + 1} / {fotos.length}</Typography>
          <IconButton aria-label="Sluiten" onClick={() => { opslaan(); onSluit(); }} sx={{ ml: 'auto' }} size="small"><CloseIcon /></IconButton>
        </Box>
        <Box sx={{ position: 'relative', textAlign: 'center' }}>
          <Box component="img" src={foto.url || foto.thumbUrl} alt={foto.bijschrift || 'Locatiefoto'}
            onError={api.linkVerlopen}
            sx={{ maxWidth: '100%', maxHeight: '68vh', objectFit: 'contain', borderRadius: 1, display: 'inline-block' }} />
          {index > 0 && (
            <IconButton aria-label="Vorige foto" onClick={() => { opslaan(); onIndex(index - 1); }}
              sx={{ position: 'absolute', left: 4, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.85)' }}>
              <VorigeIcon />
            </IconButton>
          )}
          {index < fotos.length - 1 && (
            <IconButton aria-label="Volgende foto" onClick={() => { opslaan(); onIndex(index + 1); }}
              sx={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.85)' }}>
              <VolgendeIcon />
            </IconButton>
          )}
        </Box>
        {bewerkbaar ? (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1.5 }}>
            <TextField size="small" fullWidth placeholder="Bijschrift, bijv. ingang laadperron" value={tekst}
              inputProps={{ maxLength: 300 }}
              onChange={(e) => setTekst(e.target.value)} onBlur={opslaan}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }} />
            <Button color="inherit" startIcon={<DeleteIcon />} sx={{ color: GRIJS, flexShrink: 0, textTransform: 'none' }}
              onClick={async () => {
                const weg = await api.verwijder(foto);
                if (weg) { if (fotos.length <= 1) onSluit(); else onIndex(Math.max(0, index - 1)); }
              }}>
              Verwijderen
            </Button>
          </Box>
        ) : foto.bijschrift ? (
          <Typography variant="body2" sx={{ mt: 1.5, color: GRIJS }}>{foto.bijschrift}</Typography>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Rij thumbnails voor één locatie of planningsregel. Slepen = volgorde, klikken = vergroten,
 * bestanden erop laten vallen of "Foto's" kiezen = toevoegen.
 *
 * `compact`: kleine variant voor in de planningstabel (max `max` zichtbaar, rest als +n).
 */
export function FotoStrook({ api, doel, bewerkbaar = true, compact = false, max = compact ? 2 : 12, label = "Foto's" }) {
  const fotos = api.voor(doel);
  const [open, setOpen] = useState(null);
  const [sleep, setSleep] = useState(null);          // id die versleept wordt
  const [erboven, setErboven] = useState(false);      // bestanden boven de strook
  const invoer = useRef(null);
  const bezig = api.bezig[doelSleutel(doel)];
  const maat = compact ? { w: 40, h: 30 } : { w: 72, h: 54 };
  const zichtbaar = fotos.slice(0, max);
  const rest = fotos.length - zichtbaar.length;

  const kies = () => invoer.current?.click();
  const opBestanden = (lijst) => { if (lijst?.length) api.upload(doel, lijst); };

  const dropOpFoto = (doelId) => {
    if (sleep == null || sleep === doelId) return;
    const ids = fotos.map((f) => f.id).filter((i) => i !== sleep);
    ids.splice(ids.indexOf(doelId), 0, sleep);
    api.herorden(doel, ids);
  };

  return (
    <Box
      onDragOver={(e) => { if (bewerkbaar && e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); setErboven(true); } }}
      onDragLeave={() => setErboven(false)}
      onDrop={(e) => {
        if (!bewerkbaar || !e.dataTransfer?.files?.length) return;
        e.preventDefault(); e.stopPropagation(); setErboven(false); opBestanden(e.dataTransfer.files);
      }}
      sx={{
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: compact ? 0.5 : 0.75,
        borderRadius: 1, outline: erboven ? `1px dashed ${GRIJS}` : 'none', outlineOffset: 2,
        minHeight: compact ? 30 : undefined,
      }}>
      {zichtbaar.map((f, i) => (
        <Tooltip key={f.id} title={f.bijschrift || (bewerkbaar ? 'Klik om te vergroten · sleep om te ordenen' : 'Klik om te vergroten')}>
          <Box component="img" src={f.thumbUrl || f.url} alt={f.bijschrift || 'Locatiefoto'} loading="lazy"
            draggable={bewerkbaar && fotos.length > 1}
            onDragStart={(e) => { setSleep(f.id); e.dataTransfer.effectAllowed = 'move'; }}
            onDragEnd={() => setSleep(null)}
            onDragOver={(e) => { if (sleep != null) e.preventDefault(); }}
            onDrop={(e) => { if (sleep != null) { e.preventDefault(); e.stopPropagation(); dropOpFoto(f.id); setSleep(null); } }}
            onClick={() => setOpen(i)}
            onError={api.linkVerlopen}
            sx={{
              width: maat.w, height: maat.h, objectFit: 'cover', borderRadius: 0.5, cursor: 'pointer',
              border: `1px solid ${RAND}`, opacity: sleep === f.id ? 0.4 : 1, bgcolor: '#f8fafc',
            }} />
        </Tooltip>
      ))}
      {rest > 0 && (
        <Typography component="button" type="button" onClick={() => setOpen(max)} variant="caption"
          sx={{ border: 0, bgcolor: 'transparent', color: GRIJS, cursor: 'pointer', p: 0, font: 'inherit', fontSize: 12 }}>
          +{rest}
        </Typography>
      )}
      {bezig ? (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: GRIJS }}>
          <CircularProgress size={12} color="inherit" />
          <Typography variant="caption">{bezig}</Typography>
        </Box>
      ) : bewerkbaar ? (
        compact ? (
          <Tooltip title="Foto's van deze plek toevoegen (of erop slepen) — jpg, png, heic, webp, max 10 MB">
            <IconButton size="small" aria-label="Foto toevoegen" onClick={kies} sx={{ p: 0.25, color: '#94a3b8' }}>
              <AddPhotoIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Kies foto's of sleep ze hierheen — jpg, png, heic, webp, max 10 MB per foto">
            <Button size="small" onClick={kies} startIcon={<AddPhotoIcon fontSize="small" />}
              sx={{ textTransform: 'none', color: GRIJS }}>
              {fotos.length ? 'Toevoegen' : label}
            </Button>
          </Tooltip>
        )
      ) : null}
      {bewerkbaar && (
        <input ref={invoer} type="file" hidden multiple accept={FOTO_ACCEPT} data-testid="foto-invoer"
          onChange={(e) => { opBestanden(e.target.files); e.target.value = ''; }} />
      )}
      {open != null && (
        <FotoLightbox fotos={fotos} index={Math.min(open, fotos.length - 1)} onIndex={setOpen}
          onSluit={() => setOpen(null)} api={api} bewerkbaar={bewerkbaar} />
      )}
    </Box>
  );
}

const naamVan = (l) => String(l?.fields?.['Naam locatie'] || l?.fields?.Naam || '').trim().toLowerCase();

/**
 * Fotocel in de planning: eigen foto's van de regel; heeft de regel er geen maar de locatie wel,
 * dan één thumbnail van die locatie (alleen kijken). Toevoegen kan als de regel een locatie heeft.
 */
export function PlanningFotoCel({ api, regel, locaties = [], bewerkbaar = true }) {
  const eigen = api.voor({ planningId: regel.id });
  const locNaam = String(regel?.fields?.Locatie || '').trim();
  const loc = locNaam ? locaties.find((l) => naamVan(l) === locNaam.toLowerCase()) : null;
  const vanLocatie = loc ? api.voor({ locatieId: loc.id }) : [];
  if (!eigen.length && !locNaam) return null;
  if (!eigen.length && vanLocatie.length) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <FotoStrook api={api} doel={{ locatieId: loc.id }} bewerkbaar={false} compact max={1} />
        {bewerkbaar && <FotoStrook api={api} doel={{ planningId: regel.id }} compact />}
      </Box>
    );
  }
  return <FotoStrook api={api} doel={{ planningId: regel.id }} bewerkbaar={bewerkbaar && (!!locNaam || eigen.length > 0)} compact />;
}

/** "Exporteren naar Word" — haalt de .docx op met het token en biedt hem aan als download. */
export function WordExportKnop({ callsheetId, fields, variant = 'outlined', sx, metIcoon = true }) {
  const [bezig, setBezig] = useState(false);
  const exporteer = async () => {
    setBezig(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${encodeURIComponent(callsheetId)}/word`);
      if (!r.ok) await leesJson(r, 'Word-export');
      const blob = await r.blob();
      const naam = naamUitHeader(r.headers.get('Content-Disposition')) || wordBestandsnaam(fields);
      if (window.showSaveFilePicker) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: naam,
            types: [{ description: 'Word-document', accept: { 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'] } }],
          });
          const schrijf = await handle.createWritable();
          await schrijf.write(blob);
          await schrijf.close();
          return;
        } catch (e) {
          if (e?.name === 'AbortError') return;
        }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = naam;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      meldFout(e, 'Word-export');
    } finally {
      setBezig(false);
    }
  };
  return (
    <Tooltip title="Bewerkbaar Word-document met dezelfde inhoud als de PDF, plus de locatiefoto's">
      <span>
        <Button variant={variant} onClick={exporteer} disabled={bezig || !callsheetId}
          startIcon={bezig ? <CircularProgress size={14} color="inherit" /> : (metIcoon ? <WordIcon /> : null)} sx={sx}>
          {bezig ? 'Word maken…' : 'Exporteren naar Word'}
        </Button>
      </span>
    </Tooltip>
  );
}
